// src/lib/onboarding/user-profile-prefill.ts
import type { User } from '@supabase/supabase-js';
import type { Profile } from '@/lib/supabase/types';

export interface UserProfilePrefill {
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl?: string;
  headline?: string;
  currentCompany?: string;
}

/** Splits a display name into `{ firstName, lastName }` — first token is the first
 * name, everything after is the last name (handles multi-word surnames). */
function splitFullName(fullName: string): { firstName: string; lastName: string } {
  const trimmed = fullName.trim();
  if (!trimmed) return { firstName: '', lastName: '' };
  const [firstName, ...rest] = trimmed.split(/\s+/);
  return { firstName, lastName: rest.join(' ') };
}

/**
 * Builds the prefill for the restored `startup-personal` user-profile confirm
 * step (Phase 1, Task 1.1 — docs/superpowers/plans/2026-07-09-startup-member-
 * onboarding.md). Pure — no I/O, so it's directly testable and reusable from
 * both the screen and (later) the claim-authorization matrix.
 *
 * Sourcing mirrors `auth.tsx`'s `linkedInProfileData` extraction (~line 440):
 * name/email/avatar priority is user_metadata → LinkedIn identity_data. Once
 * the user has confirmed their name (persisted to `profiles.first_name` /
 * `last_name`), that takes priority over the raw LinkedIn name on every
 * subsequent load — we don't want to clobber an edited name.
 *
 * `headline` / `currentCompany` are LinkedIn-only signals (there's no
 * equivalent for a plain email signup, and the base LinkedIn OIDC scope
 * Supabase requests doesn't always populate them) — left `undefined` when
 * unavailable rather than guessed at, so the confirm form shows blank fields
 * instead of stale/wrong data.
 */
export function buildUserProfilePrefill(
  profile: Profile | null | undefined,
  authUser: User | null | undefined
): UserProfilePrefill {
  const email = profile?.email || authUser?.email || '';

  const linkedInIdentity = authUser?.identities?.find(
    (identity) => identity.provider === 'linkedin_oidc'
  );

  if (!linkedInIdentity) {
    return {
      firstName: profile?.first_name || '',
      lastName: profile?.last_name || '',
      email,
      avatarUrl: undefined,
      headline: undefined,
      currentCompany: undefined,
    };
  }

  const identityData = (linkedInIdentity.identity_data || {}) as Record<string, unknown>;
  const userMetadata = (authUser?.user_metadata || {}) as Record<string, unknown>;

  const linkedInFullName =
    (userMetadata.full_name as string | undefined) ||
    (userMetadata.name as string | undefined) ||
    (identityData.name as string | undefined) ||
    '';
  const { firstName: linkedInFirstName, lastName: linkedInLastName } = splitFullName(linkedInFullName);

  return {
    // A previously-confirmed profile name wins over the raw LinkedIn name.
    firstName: profile?.first_name || linkedInFirstName,
    lastName: profile?.last_name || linkedInLastName,
    email,
    avatarUrl:
      (userMetadata.avatar_url as string | undefined) ||
      (userMetadata.picture as string | undefined) ||
      (identityData.picture as string | undefined) ||
      undefined,
    headline: (identityData.headline as string | undefined) || undefined,
    currentCompany:
      (identityData.current_company as string | undefined) ||
      (identityData.company as string | undefined) ||
      undefined,
  };
}
